import { useEffect, useMemo, useRef, useState } from 'react';
import ImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
// import stylesheet if you're not already using CSS @import
import 'react-image-gallery/styles/css/image-gallery.css';
import StyledButton from '../styled/StyledButton';
import { XIcon } from 'lucide-react';
import { useOriginalCircuitImages } from '@/hooks/useCircuits';
import { useTranslation } from '@/i18n';

interface CircuitDetailsImageGalleryProps {
  circuitId: string;
  images: string[];
  onClose: () => void;
  accessToken?: string;
}

const CircuitDetailsImageGallery = ({
  circuitId,
  images,
  onClose,
  accessToken,
}: CircuitDetailsImageGalleryProps) => {
  const { t } = useTranslation();
  const { data: originalCircuitImages, isSuccess } = useOriginalCircuitImages(
    circuitId,
    accessToken,
  );

  const displayedImages = useMemo(
    () =>
      images.map(
        (image, index) =>
          ({
            original: isSuccess ? originalCircuitImages[index] : image,
            thumbnail: image,
            loading: 'lazy',
            thumbnailLoading: 'lazy',
            originalClass: 'rounded-lg',
            renderThumbInner: (item) => <ThumbnailItem item={item} />,
          }) as ReactImageGalleryItem,
      ),
    [images, isSuccess, originalCircuitImages],
  );

  const [isGalleryFullScreen, setIsGalleryFullScreen] = useState(true);
  const ref = useRef<ImageGallery>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.fullScreen();
    }
  }, []);

  useEffect(() => {
    if (!isGalleryFullScreen && ref.current) onClose();
  }, [isGalleryFullScreen, onClose]);

  return (
    <div className="z-50">
      <ImageGallery
        items={displayedImages}
        lazyLoad
        ref={ref}
        showPlayButton={false}
        useBrowserFullscreen={false}
        onClick={onClose}
        renderFullscreenButton={(onClick, isFullscreen) => {
          if (ref.current) setIsGalleryFullScreen(isFullscreen);
          return isFullscreen ? (
            <div className="fixed top-8 right-10">
              <StyledButton
                variant="link"
                title={t('circuits.editorial.details.close')}
                className="text-white text-base font-medium"
                icon={<XIcon />}
                onClick={(e) => {
                  onClick(e);
                  onClose();
                }}
              />
            </div>
          ) : null;
        }}
      />
    </div>
  );
};

export default CircuitDetailsImageGallery;

const ThumbnailItem = ({ item }: { item: ReactImageGalleryItem }) => (
  <span className="image-gallery-thumbnail-inner h-16">
    <img
      className="image-gallery-thumbnail-image h-full w-auto"
      loading="lazy"
      src={item.thumbnail}
    />
  </span>
);
